/**
 * Competitors Assessment renderer (artifact_name: `competitors_assessment`,
 * tool `tool_competitors_assessment`). Renders CompetitorsAssessmentOutput:
 * per-competitor blocks (job performance, Kano features, funding, pricing,
 * positioning gap) + a differentiation map. Model = the parsed JSON (lossless).
 */

import React from 'react';
import { CardShell } from './shared/components';
import {
  ActionsBlock,
  Divided,
  KV,
  Pill,
  Prose,
  Section,
  SourcesBlock,
  Table,
  actionsHtml,
  isRecord,
  makeNarrativeEditor,
  proseHtml,
  sectionHtml,
  sourcesHtml,
  str,
  strArr,
} from './shared/atlas';
import { htmlDoc } from './shared/html';
import type { ArtifactRenderer } from './types';

export type CompetitorsModel = Record<string, unknown>;

const arr = (v: unknown): Record<string, unknown>[] => (Array.isArray(v) ? v.filter(isRecord) : []);

// -- parse / serialize -------------------------------------------------------

export function parseCompetitors(json: Record<string, unknown>): CompetitorsModel | null {
  if (!Array.isArray(json.competitors)) return null;
  if (!isRecord(json.differentiation_map) && !Array.isArray(json.competitors_assessed)) return null;
  if (arr(json.competitors).length === 0) return null;
  return json;
}

const competitorsToJson = (m: CompetitorsModel): Record<string, unknown> => m;

// -- card --------------------------------------------------------------------

const CompetitorBlock: React.FC<{ c: Record<string, unknown> }> = ({ c }) => {
  const jobPerf = arr(c.job_performance);
  const features = arr(c.feature_classification);
  const funding = isRecord(c.funding_traction) ? c.funding_traction : null;
  const pricing = isRecord(c.pricing_distribution) ? c.pricing_distribution : {};
  return (
    <div className="rounded-xl border border-gray-200 p-4 dark:border-gray-700 bg-surface">
      <h4 className="mb-2 text-sm font-semibold text-gray-900 dark:text-gray-100">
        {str(c.competitor_name)}
      </h4>
      <Prose>{str(c.product_reality)}</Prose>
      {jobPerf.length > 0 && (
        <div className="mt-3">
          <Table
            head={['Dimension', 'Performance', 'Evidence']}
            rows={jobPerf.map((d) => [
              str(d.dimension),
              <Pill value={str(d.performance)} />,
              <span className="text-xs text-gray-500 dark:text-gray-400">{str(d.evidence)}</span>,
            ])}
          />
        </div>
      )}
      {features.length > 0 && (
        <div className="mt-3">
          <Table
            head={['Feature', 'Kano', 'Why']}
            rows={features.map((f) => [
              str(f.feature),
              <Pill value={str(f.classification)} />,
              <span className="text-xs text-gray-500 dark:text-gray-400">{str(f.why)}</span>,
            ])}
          />
        </div>
      )}
      <div className="mt-3">
        <Divided>
          {funding && (
            <KV label="Funding / traction">
              <div className="flex flex-col gap-1">
                <span>{str(funding.funding)}</span>
                {strArr(funding.traction_signals).length > 0 && (
                  <ul className="list-disc ps-4 text-xs text-gray-500 dark:text-gray-400">
                    {strArr(funding.traction_signals).map((s, i) => (
                      <li key={i}>{s}</li>
                    ))}
                  </ul>
                )}
              </div>
            </KV>
          )}
          {(str(pricing.pricing_model) || str(pricing.distribution_model)) && (
            <KV label="Pricing / distribution">
              {[str(pricing.pricing_model), str(pricing.distribution_model)]
                .filter(Boolean)
                .join(' · ')}
            </KV>
          )}
          {str(c.positioning_gap) && (
            <KV label="Positioning gap">
              <Prose>{str(c.positioning_gap)}</Prose>
            </KV>
          )}
        </Divided>
      </div>
    </div>
  );
};

const CompetitorsCard: React.FC<{ model: CompetitorsModel }> = ({ model }) => {
  const competitors = arr(model.competitors);
  const diffMap = isRecord(model.differentiation_map) ? model.differentiation_map : {};
  const dims = arr(diffMap.dimensions);
  const assessed = strArr(model.competitors_assessed);

  return (
    <CardShell testId="competitors-artifact-card">
      <Section
        title="Competitors Assessment"
        trailing={
          assessed.length ? (
            <span className="text-xs text-gray-400">{assessed.length} assessed</span>
          ) : undefined
        }
      >
        <Divided>
          {str(model.job_anchor) && (
            <KV label="Job anchor">
              <Prose>{str(model.job_anchor)}</Prose>
            </KV>
          )}
          {str(model.scope_anchor) && (
            <KV label="Scope anchor">
              <Prose>{str(model.scope_anchor)}</Prose>
            </KV>
          )}
          {assessed.length > 0 && (
            <KV label="Assessed">
              <div className="flex flex-wrap gap-2">
                {assessed.map((n, i) => (
                  <span
                    key={i}
                    className="inline-flex items-center rounded-md border border-gray-200 bg-gray-50 px-2 py-0.5 text-xs text-gray-700 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-200"
                  >
                    {n}
                  </span>
                ))}
              </div>
            </KV>
          )}
        </Divided>
      </Section>

      {competitors.map((c, i) => (
        <CompetitorBlock key={i} c={c} />
      ))}

      {(dims.length > 0 || str(diffMap.verdict)) && (
        <Section title="Differentiation map">
          {dims.length > 0 && (
            <div className="mb-3">
              <Table
                head={['Dimension', 'Competitor coverage', 'Founder opportunity', 'Type']}
                rows={dims.map((d) => [
                  str(d.dimension),
                  str(d.competitor_coverage),
                  str(d.founder_opportunity),
                  <Pill value={str(d.type)} />,
                ])}
              />
            </div>
          )}
          {str(diffMap.verdict) && <Prose>{str(diffMap.verdict)}</Prose>}
        </Section>
      )}

      <SourcesBlock sources={model.sources} />
      <ActionsBlock actions={model.actions} />
    </CardShell>
  );
};

// -- editor ------------------------------------------------------------------

const CompetitorsEditor = makeNarrativeEditor<CompetitorsModel>(
  [
    {
      label: 'Job anchor',
      rows: 2,
      get: (m) => str(m.job_anchor),
      set: (m, v) => (m.job_anchor = v),
    },
    {
      label: 'Scope anchor',
      rows: 2,
      get: (m) => str(m.scope_anchor),
      set: (m, v) => (m.scope_anchor = v),
    },
    {
      label: 'Differentiation verdict',
      rows: 3,
      get: (m) => (isRecord(m.differentiation_map) ? str(m.differentiation_map.verdict) : ''),
      set: (m, v) => {
        if (isRecord(m.differentiation_map)) m.differentiation_map.verdict = v;
      },
    },
    {
      label: 'Actions',
      rows: 4,
      hint: 'one per line',
      get: (m) => (Array.isArray(m.actions) ? (m.actions as string[]).join('\n') : ''),
      set: (m, v) =>
        (m.actions = v
          .split('\n')
          .map((s) => s.trim())
          .filter(Boolean)),
    },
  ],
  competitorsToJson
);

// -- markdown / html ---------------------------------------------------------

export function competitorsToMarkdown(model: CompetitorsModel): string {
  const lines: string[] = ['# Competitors Assessment'];
  if (str(model.job_anchor)) lines.push(`**Job anchor:** ${str(model.job_anchor)}`);
  arr(model.competitors).forEach((c) => {
    lines.push(`\n## ${str(c.competitor_name)}\n\n${str(c.product_reality)}`);
    if (str(c.positioning_gap)) lines.push(`\n_Positioning gap:_ ${str(c.positioning_gap)}`);
  });
  const diffMap = isRecord(model.differentiation_map) ? model.differentiation_map : {};
  if (str(diffMap.verdict)) lines.push(`\n## Differentiation verdict\n\n${str(diffMap.verdict)}`);
  const actions = Array.isArray(model.actions) ? (model.actions as string[]) : [];
  if (actions.length) lines.push(`\n## Actions\n\n${actions.map((a) => `- ${a}`).join('\n')}`);
  return lines.join('\n');
}

export function competitorsToHtml(
  model: CompetitorsModel,
  title = 'Competitors Assessment'
): string {
  const diffMap = isRecord(model.differentiation_map) ? model.differentiation_map : {};
  const blocks = arr(model.competitors)
    .map((c) =>
      sectionHtml(
        str(c.competitor_name),
        proseHtml(str(c.product_reality)) + proseHtml(str(c.positioning_gap))
      )
    )
    .join('');
  const body = [
    sectionHtml(
      'Competitors Assessment',
      proseHtml(str(model.job_anchor)) + proseHtml(str(model.scope_anchor))
    ),
    blocks,
    str(diffMap.verdict)
      ? sectionHtml('Differentiation verdict', proseHtml(str(diffMap.verdict)))
      : '',
    sourcesHtml(model.sources),
    actionsHtml(model.actions),
  ].join('');
  return htmlDoc(title, body);
}

export const competitorsRenderer: ArtifactRenderer<CompetitorsModel> = {
  type: 'competitors_assessment',
  matchNames: ['competitors_assessment', 'competitor_assessment', 'competitors'],
  parse: parseCompetitors,
  toJson: competitorsToJson,
  toMarkdown: competitorsToMarkdown,
  toHtml: competitorsToHtml,
  Card: CompetitorsCard,
  Editor: CompetitorsEditor,
};
